import * as React from "react";
import { useState, useRef } from "react";
import { loadLoginAccess, storeLoginAccess } from "services/utils/loginAccess";
import LoginLogo from "../../../assets/img/brand/Logo.png";
import { adminRoleName } from "services/constants";
import { useForm } from "react-hook-form";
import * as Mui from "@mui/material";
import { InputAdornment } from "@material-ui/core";
import {
  Mail,
  LockOutlined,
  Visibility,
  VisibilityOff,
  Phone,
  PersonOutlined,
  DashboardOutlined,
  SecurityOutlined,
  BarChartOutlined,
  ArrowOutward,
  AutoAwesomeOutlined,
  ShieldOutlined,
  DescriptionOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { userManagementError } from "services/constants/ErrorMessages";
import "./Login.css";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { TenantDetails } from "./TenantDetails";
import { getAccessBasedRoute } from "services/utils/accessedBasedRoute";
import { supportLabel } from "services/constants";
import {
  AuthLayout,
  AuthLeftPanel,
  AuthRightPanel,
  AuthCard,
  FeatureTimeline,
} from "./AuthShell";
import {
  headingSx,
  fieldLabelSx,
  inputRootSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "./brandTokens";
import {
  parseJsonSafely,
  responseErrorMessage,
  BackendRequestError,
} from "./authErrors";

const SIGN_IN_ERROR =
  "Couldn't sign in. Please check your details and try again.";
const CLEAR_CACHE_ERROR =
  "Something went wrong. Please clear your browser cache and try again.";
const ACCOUNT_NOT_FOUND_ERROR =
  "We couldn't find an account with those details. Please check and try again.";
const GENERIC_SIGN_IN_ERROR = "Couldn't sign you in. Please try again.";

const COUNTRY_CODES = [
  { label: "🇮🇳 +91", value: "+91" },
  { label: "🇸🇬 +65", value: "+65" },
  { label: "🇬🇧 +44", value: "+44" },
  { label: "🇦🇪 +971", value: "+971" },
  { label: "🇺🇸 +1", value: "+1" },
  { label: "🇩🇪 +49", value: "+49" },
  { label: "🇲🇾 +60", value: "+60" },
  { label: "🇦🇺 +61", value: "+61" },
];
const detectIdentifierType = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.includes("@")) return "email";
  const digits = trimmed.replace(/[\s+()-]/g, "");
  if (/^\d{5,}$/.test(digits) || trimmed.startsWith("+")) return "phone";
  return null;
};

export const Login = () => {
  const domainUrl = useSelector((state) => state.tokenReducer.domain);
  const history = useHistory();
  const [showTenantDetails, setShowTenantDetails] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tenantDetails, setTenantDetails] = useState([]);
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState(null);
  const [identifierError, setIdentifierError] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const identifierInputRef = useRef(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      password: "",
    },
  });

  const handleIdentifierChange = (e) => {
    const val = e.target.value;
    setIdentifier(val);
    setIdentifierType(detectIdentifierType(val));
    if (identifierError) {
      setIdentifierError("");
    }
  };

  const validateIdentifierBeforeSubmit = () => {
    const rawIdentifier =
      identifierInputRef.current?.value !== undefined
        ? identifierInputRef.current.value
        : identifier;
    const normalizedIdentifier = rawIdentifier.trim();
    const normalizedIdentifierType = detectIdentifierType(normalizedIdentifier);

    setIdentifier(rawIdentifier);
    setIdentifierType(normalizedIdentifierType);

    if (!normalizedIdentifier) {
      setIdentifierError("Email or mobile number is required");
      return null;
    }

    if (!normalizedIdentifierType) {
      setIdentifierError("Enter a valid email address or mobile number");
      return null;
    }

    setIdentifierError("");
    return {
      normalizedIdentifier,
      normalizedIdentifierType,
    };
  };

  const getTenantDetails = async (normalizedId, data) => {
    let url =
      identifierType === "email"
        ? `${domainUrl}user_tenant/details/?email=${normalizedId.toLowerCase()}`
        : `${domainUrl}user_tenant/details/?phone_number=${normalizedId}`;
    const userResponse = await fetch(url, {
      method: "GET",
      headers: {
        "content-type": "application/json",
      },
    });
    const resData = await parseJsonSafely(userResponse);
    if (userResponse?.ok) {
      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        await loginInLocalEnvironment(resData, normalizedId, data);
      } else {
        setShowTenantDetails(true);
        setTenantDetails(resData);
      }
    } else {
      showToast(
        "error",
        responseErrorMessage(
          userResponse,
          resData,
          userResponse.status === 403 ? CLEAR_CACHE_ERROR : SIGN_IN_ERROR,
        ),
      );
    }
  };

  const loginInLocalEnvironment = async (resData, normalizedId, data) => {
    // On localhost there is no real domain to match the returned tenant
    // rows against (domainUrl is always http://localhost:8000/api/), so a
    // domain_name comparison here would reject every genuine account. Treat
    // every tenant row the account has access to as usable instead.
    let filterRow = Array.isArray(resData) ? resData : [];
    if (filterRow.length !== 0) {
      let url =
        identifierType === "email"
          ? `${domainUrl}login/email_verfication/?email=${normalizedId.toLowerCase()}`
          : `${domainUrl}phone_verfication/?phone_no=${normalizedId}`;
      const emailResponse = await fetch(url, {
        method: "GET",
        headers: {
          "content-type": "application/json",
        },
      });
      const emailReturnResponse = await parseJsonSafely(emailResponse);
      if (
        emailResponse.status === 200 &&
        emailReturnResponse.user_status === "Active"
      ) {
        localStorage.clear();
        const userId = emailReturnResponse.user_id;
        const userResponse = await fetch(`${domainUrl}custom-token-auth/`, {
          method: "POST",
          headers: {
            "content-type": "application/json",
          },
          body: JSON.stringify({
            username: emailReturnResponse.emailuser.username,
            password: filterRow[0].tenant_password,
          }),
        });
        const userReturnResponse = await parseJsonSafely(userResponse);
        if (userResponse.status !== 200) {
          showToast(
            "error",
            responseErrorMessage(userResponse, userReturnResponse, SIGN_IN_ERROR),
          );
          setIsLoading(false);
        } else {
          const userData = {
            id: userId,
            first_name: emailReturnResponse.emailuser.first_name,
            username: emailReturnResponse.emailuser.username,
            last_name: emailReturnResponse.emailuser.last_name,
            email: emailReturnResponse.emailuser.email,
            type: emailReturnResponse.type,
            phonenumber: emailReturnResponse?.type?.phonenumber,
            approver: emailReturnResponse.approver,
            teamlead: emailReturnResponse.teamlead,
            firmName: emailReturnResponse.firm_name,
            approver_id: emailReturnResponse.approver_id,
            image_url: emailReturnResponse.image_url,
            department_id: emailReturnResponse.department_id,
            department_name: emailReturnResponse.department_name,
            employee_id: emailReturnResponse.employee_id,
            designation: emailReturnResponse.designation,
          };
          const access = await loadLoginAccess({
            domain: domainUrl,
            token: userReturnResponse.token,
            userId,
            userType: userData.type,
          });
          storeLoginAccess(access);
          localStorage.setItem("currentuser", JSON.stringify(userData));
          localStorage.setItem("token", userReturnResponse.token);
          localStorage.setItem(
            "sessionToken",
            userReturnResponse.session_token,
          );
          if ([adminRoleName, supportLabel].includes(emailReturnResponse.type)) {
            getCompanyDetails(userReturnResponse.token, emailReturnResponse);
          } else {
            reDirectPath(emailReturnResponse);
          }
        }
      } else if (
        emailReturnResponse.exists === false ||
        emailResponse.status === 400
      ) {
        showToast(
          "error",
          responseErrorMessage(
            emailResponse,
            emailReturnResponse,
            ACCOUNT_NOT_FOUND_ERROR,
          ),
        );
        setShowTenantDetails(false);
        setIsLoading(false);
      } else if (emailReturnResponse.user_status === "Inactive") {
        showToast(
          "error",
          responseErrorMessage(
            emailResponse,
            emailReturnResponse,
            userManagementError.emailReturnResponseError,
          ),
        );
        setShowTenantDetails(false);
        setIsLoading(false);
      } else {
        // Any other status (403, 500, "no portal access", ...) used to fall
        // through with no feedback at all.
        showToast(
          "error",
          responseErrorMessage(
            emailResponse,
            emailReturnResponse,
            emailResponse.status === 403 ? CLEAR_CACHE_ERROR : SIGN_IN_ERROR,
          ),
        );
        setShowTenantDetails(false);
        setIsLoading(false);
      }
    } else {
      showToast(
        "error",
        "Account not found. Please contact your administrator for help.",
      );
    }
  };

  const reDirectPath = (emailReturnResponse) => {
    let accessibleProducts = JSON.parse(localStorage.getItem("productAccess"));
    let product_name = accessibleProducts
      ? accessibleProducts[0]?.product_name
      : "default";
    const accesbasedReroutePath = getAccessBasedRoute(product_name);
    window.location.href = accesbasedReroutePath;
  };

  const onSubmit = async (data) => {
    const identifierValidation = validateIdentifierBeforeSubmit();
    if (!identifierValidation) {
      return;
    }

    const { normalizedIdentifier, normalizedIdentifierType } =
      identifierValidation;

    let normalizedId = normalizedIdentifier;
    if (normalizedIdentifierType === "phone") {
      const digitsOnly = normalizedId.replace(/[\s()-]/g, "");
      normalizedId = digitsOnly.startsWith("+")
        ? digitsOnly
        : countryCode + digitsOnly;
    }

    try {
      await signIn(data, normalizedId, normalizedIdentifierType);
    } catch (error) {
      // Network failures and unexpected exceptions used to be swallowed as
      // unhandled promise rejections with no message on screen.
      console.error("[Login] sign in failed:", error);
      setIsLoading(false);
      showToast("error", GENERIC_SIGN_IN_ERROR);
    }
  };

  const signIn = async (data, normalizedId, normalizedIdentifierType) => {
    let url =
      normalizedIdentifierType === "email"
        ? `${domainUrl}login/email_verfication_public/?email=${normalizedId.toLowerCase()}`
        : `${domainUrl}login/email_verfication_public/?phonenumber=${normalizedId}`;
    const emailResponse = await fetch(url, {
      method: "GET",
      headers: {
        "content-type": "application/json",
      },
    });
    const emailReturnResponse = await parseJsonSafely(emailResponse);
    if (emailResponse.ok) {
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname.startsWith("127.");

      function getCSRFToken() {
        return document.cookie
          .split("; ")
          .find((row) => row.startsWith("csrftoken="))
          ?.split("=")[1];
      }

      let userResponse;

      if (!isLocalhost) {
        await fetch(`${domainUrl}csrf/`, { credentials: "include" });

        userResponse = await fetch(`${domainUrl}custom-token-auth-public/`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCSRFToken(),
          },
          body: JSON.stringify({
            username: emailReturnResponse.username,
            password: data.password,
          }),
        });
      } else {
        userResponse = await fetch(`${domainUrl}custom-token-auth-public/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": getCSRFToken(),
          },
          body: JSON.stringify({
            username: emailReturnResponse.username,
            password: data.password,
          }),
        });
      }
      if (userResponse?.ok) {
        await getTenantDetails(normalizedId, data);
      } else {
        const userReturnResponse = await parseJsonSafely(userResponse);
        showToast(
          "error",
          responseErrorMessage(
            userResponse,
            userReturnResponse,
            userResponse?.status === 403
              ? CLEAR_CACHE_ERROR
              : "Incorrect details. Please try again.",
          ),
        );
      }
    } else if (
      emailReturnResponse.exists === false ||
      emailResponse.status === 400
    ) {
      showToast(
        "error",
        responseErrorMessage(
          emailResponse,
          emailReturnResponse,
          ACCOUNT_NOT_FOUND_ERROR,
        ),
      );
      setIsLoading(false);
    } else if (emailReturnResponse.user_status === "Inactive") {
      showToast(
        "error",
        responseErrorMessage(
          emailResponse,
          emailReturnResponse,
          userManagementError.emailReturnResponseError,
        ),
      );
      setIsLoading(false);
    } else {
      // 403 and any other failure (e.g. "The user has no portal access").
      // Statuses other than 400/403 used to show nothing at all.
      showToast(
        "error",
        responseErrorMessage(
          emailResponse,
          emailReturnResponse,
          emailResponse.status === 403 ? CLEAR_CACHE_ERROR : SIGN_IN_ERROR,
        ),
      );
      setIsLoading(false);
    }
  };

  const handleLoginSubmit = (event) => {
    const identifierValidation = validateIdentifierBeforeSubmit();
    if (!identifierValidation) {
      event.preventDefault();
      return;
    }

    handleSubmit(onSubmit)(event);
  };

  const getCompanyDetails = async (token, emailReturnResponse) => {
    try {
      const response = await fetch(`${domainUrl}company/`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
      });
      const data = await parseJsonSafely(response);
      if (response.ok) {
        if (data.length === 0) {
          localStorage.setItem("currentScreen", "Administration");
          localStorage.setItem("currentSubMenu", "Users");
          window.location.href = "/admin/administration/user";
        } else {
          reDirectPath(emailReturnResponse);
        }
      } else {
        throw new BackendRequestError(
          responseErrorMessage(response, data, GENERIC_SIGN_IN_ERROR),
        );
      }
    } catch (error) {
      console.error("[Login] company details request failed:", error);
      showToast(
        "error",
        error instanceof BackendRequestError
          ? error.message
          : GENERIC_SIGN_IN_ERROR,
      );
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const featureItems = [
    {
      icon: <DashboardOutlined sx={{ fontSize: 22 }} />,
      title: "Centralized Workflows",
      subtitle: "Streamline processes and keep everything in sync.",
    },
    {
      icon: <SecurityOutlined sx={{ fontSize: 22 }} />,
      title: "Enterprise Security",
      subtitle: "Bank-grade security to keep your data safe and compliant.",
    },
    {
      icon: <BarChartOutlined sx={{ fontSize: 22 }} />,
      title: "Real-time Insights",
      subtitle: "Make faster, smarter decisions with powerful analytics.",
    },
  ];

  return showTenantDetails ? (
    <TenantDetails
      setIsLoading={setIsLoading}
      groupButtonActiveTap={identifierType === "phone" ? "PhoneNo" : "Email"}
      tenantDetails={tenantDetails}
      setShowTenantDetails={setShowTenantDetails}
      setTenantDetails={setTenantDetails}
    />
  ) : (
    <React.Fragment>
      <AuthLayout>
        <AuthLeftPanel
          top={
            <Mui.Stack spacing={4}>
              <Mui.Box sx={{ maxWidth: 520 }}>
                <Mui.Typography
                  sx={{
                    fontSize: { md: 32, lg: 40, xl: 46 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                  }}
                >
                  One platform.
                  <br />
                  Every operation.
                  <br />
                  <Mui.Box component="span" sx={{ color: "#6FE0A8" }}>
                    Total clarity.
                  </Mui.Box>
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    mt: 2.5,
                    fontSize: { md: 15, lg: 17 },
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 460,
                  }}
                >
                  Unify attendance, projects, CRM, and team operations — so you
                  can focus on what truly matters.
                </Mui.Typography>
              </Mui.Box>

              <FeatureTimeline items={featureItems} />
            </Mui.Stack>
          }
          bottom={
            <Mui.Box
              sx={{
                maxWidth: 520,
                borderRadius: 3,
                border: "1px solid rgba(255,255,255,0.16)",
                background: "rgba(255,255,255,0.06)",
                px: 3,
                py: 2.5,
              }}
            >
              <Mui.Stack direction="row" spacing={2} alignItems="flex-start">
                <Mui.Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeOutlined
                    sx={{ fontSize: 18, color: "#6FE0A8" }}
                  />
                </Mui.Box>
                <Mui.Box>
                  <Mui.Stack direction="row" spacing={1} alignItems="center">
                    <Mui.Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                      OrganFlow is becoming Cauver
                    </Mui.Typography>
                    <Mui.Box
                      component="span"
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#0B3B2A",
                        background: "#6FE0A8",
                        borderRadius: 999,
                        px: 1,
                        py: 0.25,
                        lineHeight: 1.4,
                      }}
                    >
                      New
                    </Mui.Box>
                  </Mui.Stack>
                  <Mui.Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 14,
                      lineHeight: 1.4,
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    Same data and workflows, a better experience.
                  </Mui.Typography>
                  <Mui.Box
                    component="a"
                    href="https://cauver.com/"
                    sx={{
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#6FE0A8",
                      textDecoration: "none",
                      cursor: "pointer",
                    }}
                  >
                    Learn more about Cauver
                    <ArrowOutward sx={{ fontSize: 16 }} />
                  </Mui.Box>
                </Mui.Box>
              </Mui.Stack>
            </Mui.Box>
          }
        />

        <AuthRightPanel>
          <form
            onSubmit={handleLoginSubmit}
            style={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <AuthCard>
              <Mui.Box sx={{ display: "flex", justifyContent: "center" }}>
                <Mui.Box
                  component="img"
                  src={LoginLogo}
                  alt="OrganFlow mark"
                  sx={{ width: 64, height: 64, objectFit: "contain" }}
                />
              </Mui.Box>

              <Mui.Typography sx={{ ...headingSx, mt: 2, textAlign: "center" }}>
                Welcome back
              </Mui.Typography>
              <Mui.Typography
                sx={{
                  mt: 0.75,
                  textAlign: "center",
                  fontSize: 15,
                  lineHeight: 1.4,
                  color: "rgba(21,23,26,0.6)",
                }}
              >
                Sign in to your OrganFlow workspace
              </Mui.Typography>

              <Mui.Box sx={{ mt: 3.5, width: "100%" }}>
                <Mui.Typography sx={fieldLabelSx}>
                  Email or mobile
                </Mui.Typography>
                <Mui.TextField
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": inputRootSx,
                    "& .MuiOutlinedInput-input": {
                      py: 1.75,
                      pr: 1.5,
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 100px #fff inset",
                      WebkitTextFillColor: "#15171a",
                      caretColor: "#15171a",
                      borderRadius: "inherit",
                    },
                  }}
                  size="medium"
                  type="text"
                  placeholder="Enter your email or mobile"
                  value={identifier}
                  onChange={handleIdentifierChange}
                  error={Boolean(identifierError)}
                  helperText={identifierError}
                  inputRef={identifierInputRef}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {identifierType === "phone" ? (
                          <>
                            <Phone color="primary" sx={{ mr: 0.5 }} />
                            <Mui.Select
                              value={countryCode}
                              onChange={(e) => setCountryCode(e.target.value)}
                              variant="standard"
                              disableUnderline
                              sx={{
                                fontSize: 15,
                                fontWeight: 500,
                                color: "primary.main",
                                minWidth: 70,
                                mr: 1,
                                "& .MuiSelect-icon": {
                                  color: "primary.main",
                                },
                              }}
                            >
                              {COUNTRY_CODES.map((cc) => (
                                <Mui.MenuItem key={cc.value} value={cc.value}>
                                  {cc.label}
                                </Mui.MenuItem>
                              ))}
                            </Mui.Select>
                          </>
                        ) : identifierType === "email" ? (
                          <Mail color="primary" />
                        ) : (
                          <PersonOutlined color="primary" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Mui.Box>

              <Mui.Box sx={{ mt: 2.25, width: "100%" }}>
                <Mui.Typography sx={fieldLabelSx}>Password</Mui.Typography>
                <Mui.TextField
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": inputRootSx,
                    "& .MuiOutlinedInput-input": {
                      py: 1.75,
                      pr: 1.5,
                    },
                    "& input:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 100px #fff inset",
                      WebkitTextFillColor: "#15171a",
                      caretColor: "#15171a",
                      borderRadius: "inherit",
                    },
                  }}
                  size="medium"
                  placeholder="Enter your password"
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: "Password is required",
                  })}
                  error={Boolean(errors.password)}
                  helperText={errors.password?.message}
                  InputProps={{
                    autoComplete: "current-password",
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Mui.IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </Mui.IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onAnimationStart={(e) => {
                    if (e.animationName === "onAutoFillStart") {
                      const val = e.target.value;
                      setValue("password", val, { shouldValidate: true });
                    }
                  }}
                />
              </Mui.Box>

              <Mui.Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <Mui.Typography
                  sx={{
                    cursor: "pointer",
                    fontSize: 14.5,
                    fontWeight: 600,
                    color: "#1f7a53",
                  }}
                  onClick={() => {
                    history.push("/auth/send/otp");
                    localStorage.setItem("PasswordHandling", "ForgotPassword");
                  }}
                >
                  Forgot password?
                </Mui.Typography>
              </Mui.Box>

              <Mui.Box sx={{ mt: 2.5, width: "100%" }}>
                <Mui.Button
                  variant="contained"
                  type="submit"
                  size="large"
                  sx={primaryButtonSx}
                >
                  {isLoading ? (
                    <Mui.CircularProgress size={24} color="inherit" />
                  ) : (
                    "Sign in"
                  )}
                </Mui.Button>
              </Mui.Box>

              <Mui.Box
                sx={{
                  my: "28px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <Mui.Box sx={{ flex: 1, height: "1px", bgcolor: "#E2E7E3" }} />
                <Mui.Typography
                  sx={{
                    fontSize: 13,
                    color: "#6A7B73",
                    whiteSpace: "nowrap",
                  }}
                >
                  New to OrganFlow?
                </Mui.Typography>
                <Mui.Box sx={{ flex: 1, height: "1px", bgcolor: "#E2E7E3" }} />
              </Mui.Box>

              <Mui.Box sx={{ width: "100%" }}>
                <Mui.Button
                  variant="outlined"
                  fullWidth
                  href="/auth/send/otp"
                  onClick={() => {
                    localStorage.setItem(
                      "PasswordHandling",
                      "First Time Handle",
                    );
                  }}
                  sx={secondaryButtonSx}
                >
                  Set up your password
                </Mui.Button>
              </Mui.Box>

              <Mui.Box
                sx={{
                  width: "100%",
                  mt: 3.5,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Mui.Box
                  component="a"
                  href="https://organflow.co/about.html"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    textDecoration: "none",
                  }}
                >
                  <ShieldOutlined
                    sx={{ fontSize: 16, color: "rgba(21,23,26,0.45)" }}
                  />
                  <Mui.Typography
                    sx={{
                      fontSize: 13.5,
                      color: "rgba(21,23,26,0.58)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    About us
                  </Mui.Typography>
                </Mui.Box>
                <Mui.Box
                  sx={{
                    width: "1px",
                    height: 14,
                    bgcolor: "rgba(21,23,26,0.15)",
                  }}
                />
                <Mui.Box
                  component="a"
                  href="https://organflow.co/privacy-policy.html"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.6,
                    textDecoration: "none",
                  }}
                >
                  <DescriptionOutlined
                    sx={{ fontSize: 16, color: "rgba(21,23,26,0.45)" }}
                  />
                  <Mui.Typography
                    sx={{
                      fontSize: 13.5,
                      color: "rgba(21,23,26,0.58)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Privacy Policy
                  </Mui.Typography>
                </Mui.Box>
              </Mui.Box>
            </AuthCard>
          </form>
        </AuthRightPanel>
      </AuthLayout>
    </React.Fragment>
  );
};
